/**
 * ConfirmDialog - Diálogo de confirmación con animación slide-down
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/components
 *
 * @description
 * Diálogo de confirmación que se desliza desde detrás del TopBar
 * con glassmorphism y animaciones suaves de entrada/salida.
 *
 * @remarks
 * **Animación**:
 * - Entrada: slide-down desde top: -100px → top: 80px (debajo del topbar)
 * - Salida: slide-up desde top: 80px → top: -100px
 * - Duración: 300ms con ease-out
 *
 * @example
 * ```tsx
 * const confirmed = await ConfirmDialog.show({
 *   title: '¿Descartar cambios?',
 *   message: 'Los datos ingresados se perderán.',
 *   confirmText: 'Aceptar',
 *   cancelText: 'Cancelar',
 * });
 * ```
 */

import { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertCircle } from 'lucide-react';
import './confirm-dialog.css';

export interface ConfirmDialogOptions {
  /**
   * Título del diálogo
   */
  title: string;

  /**
   * Mensaje de confirmación
   */
  message: string;

  /**
   * Texto del botón de confirmación
   * @default 'Aceptar'
   */
  confirmText?: string;

  /**
   * Texto del botón de cancelación
   * @default 'Cancelar'
   */
  cancelText?: string;

  /**
   * Tipo de diálogo (afecta el color del ícono y botón)
   * @default 'warning'
   */
  type?: 'warning' | 'danger' | 'info';
}

interface ConfirmDialogProps extends ConfirmDialogOptions {
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialogComponent({
  title,
  message,
  confirmText = 'Aceptar',
  cancelText = 'Cancelar',
  type = 'warning',
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const [isClosing, setIsClosing] = useState(false);

  /**
   * Maneja el cierre con animación
   */
  const handleClose = (confirmed: boolean) => {
    setIsClosing(true);
    // Esperar a que termine la animación antes de ejecutar callback
    setTimeout(() => {
      if (confirmed) {
        onConfirm();
      } else {
        onCancel();
      }
    }, 300); // Duración de la animación
  };

  /**
   * Cerrar con ESC
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <>
      {/* Overlay oscuro que cubre todo */}
      <div
        className={`confirm-dialog-overlay ${isClosing ? 'closing' : ''}`}
        onClick={() => handleClose(false)}
      />

      {/* Diálogo que se desliza desde arriba */}
      <div className={`confirm-dialog ${isClosing ? 'closing' : ''}`}>
        <div className="confirm-dialog-content">
          <div className="confirm-dialog-header">
            <AlertCircle size={20} className={`confirm-dialog-icon ${type}`} />
            <h3 className="confirm-dialog-title">{title}</h3>
          </div>

          <p className="confirm-dialog-message">{message}</p>

          <div className="confirm-dialog-actions">
            <button
              className="confirm-dialog-btn confirm-dialog-btn-cancel"
              onClick={() => handleClose(false)}
            >
              {cancelText}
            </button>
            <button
              className={`confirm-dialog-btn confirm-dialog-btn-confirm ${type}`}
              onClick={() => handleClose(true)}
              autoFocus
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Muestra un diálogo de confirmación y retorna una promesa
 * que se resuelve con true (confirmado) o false (cancelado)
 */
export const ConfirmDialog = {
  show: (options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      // Crear contenedor
      const container = document.createElement('div');
      container.className = 'confirm-dialog-container';
      document.body.appendChild(container);
      const root = createRoot(container);

      const cleanup = () => {
        root.unmount();
        document.body.removeChild(container);
      };

      const handleConfirm = () => {
        cleanup();
        resolve(true);
      };

      const handleCancel = () => {
        cleanup();
        resolve(false);
      };

      // Renderizar diálogo
      root.render(
        <ConfirmDialogComponent
          {...options}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      );
    });
  },
};
